import { SMB_FY_QUARTER, SMB_QUARTERS } from '../configs'
import { ISmbNode, TSmbSplitResult, TSmbSplitStore } from '../model/types'

import { isSmbNodeExcluded } from './isSmbNodeExcluded'
import {
  getDirectStoredSplit,
  getEuPercent,
  getStoredSplit,
  setStoredSplit,
  subtreeHasAnySplit,
} from './smbSplitStore'

export const EMPTY_SMB_SPLIT: TSmbSplitResult = { isEmpty: true }

const toResult = (smb: number): TSmbSplitResult => ({
  isEmpty: false,
  smb,
  eu: getEuPercent(smb),
})

/** Financial weight of a node at a given year and grain, used to average children into their parent */
export type TGetSmbWeight = (node: ISmbNode, year: number, fq: number) => number

export interface IGetSmbEffectiveValueOptions {
  /** In quarterly mode the FY cell is calculated from Q1..Q4 even if FQuarter=5 was loaded. */
  deriveFyFromQuarters?: boolean
  /** An expanded row is calculated from its descendants, never from its own stored value. */
  ignoreDirectNodeId?: string
  /** Every expanded level in a recursive roll-up must ignore its own possibly stale direct value. */
  ignoreDirectNodeIds?: ReadonlySet<string>
  /** Historical baselines may predate some current hierarchy rows; average the rows that exist. */
  allowPartialChildren?: boolean
}

interface IWeightedAverage {
  totalWeight: number
  accumulated: number
  hasEveryValue: boolean
  hasAnyValue: boolean
  /** Plain sum and count, used when there is no financial weight to tell contributors apart */
  plainSum: number
  count: number
}

const EMPTY_AVERAGE: IWeightedAverage = {
  totalWeight: 0,
  accumulated: 0,
  hasEveryValue: true,
  hasAnyValue: false,
  plainSum: 0,
  count: 0,
}

const addToAverage = (
  acc: IWeightedAverage,
  value: TSmbSplitResult,
  weight: number
): IWeightedAverage => {
  if (value.isEmpty) {
    return { ...acc, hasEveryValue: false }
  }

  return {
    totalWeight: acc.totalWeight + weight,
    accumulated: acc.accumulated + value.smb * weight,
    hasEveryValue: acc.hasEveryValue,
    hasAnyValue: true,
    plainSum: acc.plainSum + value.smb,
    count: acc.count + 1,
  }
}

const averageOf = (
  { totalWeight, accumulated, hasEveryValue, hasAnyValue, plainSum, count }: IWeightedAverage,
  allowPartial = false
) => {
  if (!hasAnyValue || (!hasEveryValue && !allowPartial)) {
    return EMPTY_SMB_SPLIT
  }

  if (Number.isFinite(totalWeight) && totalWeight !== 0) {
    return toResult(accumulated / totalWeight)
  }

  // Everything below carries a split but no Financial Plan amount to weight it by. The level
  // is still planned, so it shows the plain average of its contributors instead of no data.
  return count ? toResult(plainSum / count) : EMPTY_SMB_SPLIT
}

/**
 * The split at a given grain: the direct entry of this node when it has one, otherwise a
 * financial-weighted roll-up of what sits below it.
 *
 * The roll-up only counts as planned when EVERY contributor resolves to a value - a partial
 * average computed from just some children (leaving the rest blank) is treated as not planned
 * at this level. That is what makes "planned" mean the same thing at every level of the tree.
 */
export const getSmbEffectiveValue = (
  store: TSmbSplitStore,
  node: ISmbNode,
  year: number,
  fq: number,
  getWeight: TGetSmbWeight,
  options: IGetSmbEffectiveValueOptions = {}
): TSmbSplitResult => {
  if (isSmbNodeExcluded(node)) {
    return EMPTY_SMB_SPLIT
  }

  const ignoresDirect =
    options.ignoreDirectNodeId === node.id || options.ignoreDirectNodeIds?.has(node.id)

  // The year grain can also be derived from the own 4 quarters of this same node, not only
  // from its children - a row planned entirely by quarter still has a real year value once
  // all 4 are in. Quarterly mode deliberately does this before reading FQuarter=5: responses
  // may contain both grains, and the FY column in that mode must reflect the visible quarters.
  if (fq === SMB_FY_QUARTER && options.deriveFyFromQuarters) {
    const fromQuarters = averageOf(
      SMB_QUARTERS.reduce<IWeightedAverage>(
        (acc, quarter) =>
          addToAverage(
            acc,
            getSmbEffectiveValue(store, node, year, quarter, getWeight, options),
            getWeight(node, year, quarter)
          ),
        EMPTY_AVERAGE
      )
    )

    if (!fromQuarters.isEmpty) {
      return fromQuarters
    }
  }

  const stored = ignoresDirect ? null : getStoredSplit(store, node.id, year, fq)
  if (stored) {
    return toResult(stored.smb)
  }

  if (fq === SMB_FY_QUARTER && !options.deriveFyFromQuarters && !ignoresDirect) {
    const fromQuarters = averageOf(
      SMB_QUARTERS.reduce<IWeightedAverage>(
        (acc, quarter) =>
          addToAverage(
            acc,
            (() => {
              const directQuarter = getDirectStoredSplit(store, node.id, year, quarter)

              return directQuarter ? toResult(directQuarter.smb) : EMPTY_SMB_SPLIT
            })(),
            getWeight(node, year, quarter)
          ),
        EMPTY_AVERAGE
      )
    )

    if (!fromQuarters.isEmpty) {
      return fromQuarters
    }
  }

  if (!node.children.length) {
    return EMPTY_SMB_SPLIT
  }

  // Rows outside the plan neither contribute a value nor make an otherwise complete parent
  // count as unplanned.
  const contributors = node.children.filter((child) => !isSmbNodeExcluded(child))
  if (!contributors.length) {
    return EMPTY_SMB_SPLIT
  }

  return averageOf(
    contributors.reduce<IWeightedAverage>(
      (acc, child) =>
        addToAverage(
          acc,
          getSmbEffectiveValue(store, child, year, fq, getWeight, options),
          getWeight(child, year, fq)
        ),
      EMPTY_AVERAGE
    ),
    options.allowPartialChildren
  )
}

/**
 * A row with nothing of its own - no direct entry, nothing below it - defaults to whatever
 * percentage is showing on the nearest ancestor that has one. The vertical counterpart of
 * the year to quarter inheritance in ensureQuarterSeed.
 */
export const ensureVerticalSeed = (
  store: TSmbSplitStore,
  node: ISmbNode,
  ancestors: ISmbNode[],
  year: number,
  fq: number,
  getWeight: TGetSmbWeight
): void => {
  if (getStoredSplit(store, node.id, year, fq) || subtreeHasAnySplit(store, node, year)) {
    return
  }

  for (let i = ancestors.length - 1; i >= 0; i--) {
    const value = getSmbEffectiveValue(store, ancestors[i], year, fq, getWeight)
    if (!value.isEmpty) {
      setStoredSplit(store, node.id, year, fq, value.smb, true)

      return
    }
  }
}

/**
 * Seeds every empty quarter of a node from the nearest ancestor default in one pass.
 * Checking "has any data" once up front matters: seeding Q1 first would otherwise flip that
 * check and block Q2-Q4 from inheriting the same default within the same pass.
 */
export const ensureVerticalSeedQuarters = (
  store: TSmbSplitStore,
  node: ISmbNode,
  ancestors: ISmbNode[],
  year: number,
  getWeight: TGetSmbWeight
): void => {
  if (subtreeHasAnySplit(store, node, year)) {
    return
  }

  SMB_QUARTERS.forEach((fq) => {
    if (getStoredSplit(store, node.id, year, fq)) {
      return
    }

    for (let i = ancestors.length - 1; i >= 0; i--) {
      const value = getSmbEffectiveValue(store, ancestors[i], year, fq, getWeight)
      if (!value.isEmpty) {
        setStoredSplit(store, node.id, year, fq, value.smb, true)
        break
      }
    }
  })
}
